import React, { useEffect, useMemo, useState } from "react";

import type { RowData } from "@tanstack/react-table-v9";

import type { PivotField } from "./types";
import { valueLabel } from "./valueUtils";
import SlideOutPanel from "../../SlideOutPanel/SlideOutPanel";
import { EXPANDER_COLUMN_ID, SELECTION_COLUMN_ID } from "../columns";
import type { TableV2ColumnDef } from "../columns";
import type { TableV2PivotDetail } from "../hooks/usePivotView";
import { useTableV2 } from "../hooks/useTableV2";
import type { TableV2Instance } from "../hooks/useTableV2";

export interface PivotDetailTableProps<TData extends RowData> {
  table: TableV2Instance<TData>;
  isLoading: boolean;
  error: string | null;
  onRetry: () => void;
  title: string;
  fileName: string;
}

const ROW_CONTROLS = new Set([SELECTION_COLUMN_ID, EXPANDER_COLUMN_ID]);

const detailTitle = <TData,>(
  detail: TableV2PivotDetail<TData>,
  fields: PivotField<TData>[],
) => {
  const { row, column, value } = detail.cell;
  const where = [...row.labels, ...column.labels].join(" › ");
  return `${valueLabel(fields, value)}: ${where}`;
};

// Only mounted while the panel is open, so the pivot doesn't build a second table on every render
const DetailTable = <TData extends RowData>({
  rows,
  columns,
  renderTable,
  ...props
}: Omit<PivotDetailTableProps<TData>, "table"> & {
  rows: TData[];
  columns: TableV2ColumnDef<TData>[];
  renderTable: (props: PivotDetailTableProps<TData>) => React.ReactNode;
}) => {
  const table = useTableV2({ data: rows, columns });
  return <>{renderTable({ table, ...props })}</>;
};

const detailFileName = <TData,>(
  detail: TableV2PivotDetail<TData>,
  fields: PivotField<TData>[],
) =>
  `${valueLabel(fields, detail.cell.value)} detail`
    .replace(/[\\/:*?"<>|]+/g, "-")
    .trim();

const PivotDetailPanel = <TData extends RowData>({
  detail,
  columns,
  fields,
  renderTable,
}: {
  detail: TableV2PivotDetail<TData> | null;
  columns: readonly TableV2ColumnDef<TData>[];
  fields: PivotField<TData>[];
  // TableV2 passes itself in, so this file doesn't import it back
  renderTable: (props: PivotDetailTableProps<TData>) => React.ReactNode;
}) => {
  const [rows, setRows] = useState<TData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [title, setTitle] = useState("");
  const [fileName, setFileName] = useState("Detail");

  const detailColumns = useMemo(
    () => columns.filter((column) => !ROW_CONTROLS.has(column.id ?? "")),
    [columns],
  );

  useEffect(() => {
    if (!detail) return;
    let current = true;
    setTitle(detailTitle(detail, fields));
    setFileName(detailFileName(detail, fields));
    setIsLoading(true);
    setError(null);
    detail
      .loadRows()
      .then((loaded) => current && setRows(loaded))
      .catch(() => current && setError("Couldn't load these rows"))
      .finally(() => current && setIsLoading(false));
    return () => {
      current = false;
    };
  }, [detail?.cell, attempt]);

  // Mounted from the first click on, so closing can slide out
  if (!detail && !title) return null;

  return (
    <SlideOutPanel
      size={3}
      isOpened={!!detail}
      setIsOpened={(open) => !open && detail?.close()}
      title={title}
      subTitle={
        isLoading
          ? undefined
          : `${rows.length.toLocaleString()} ${rows.length === 1 ? "row" : "rows"}`
      }
    >
      {detail && (
        <DetailTable
          rows={rows}
          columns={detailColumns}
          renderTable={renderTable}
          isLoading={isLoading}
          error={error}
          onRetry={() => setAttempt((n) => n + 1)}
          title={title}
          fileName={fileName}
        />
      )}
    </SlideOutPanel>
  );
};

export default PivotDetailPanel;
